import { SubscriptionWizardDialog } from './SubscriptionWizardDialog';
import { DowngradeDialog } from './DowngradeDialog';
import { UpgradeRequiredDialog } from './UpgradeRequiredDialog';
import { AddonRequiredDialog } from './AddonRequiredDialog';
import { AddonUpdateDialog } from './AddonUpdateDialog';
import { UnpaidInvoicesDialog } from './UnpaidInvoicesDialog';
import { EnableTeamBillingDialog } from './EnableTeamBillingDialog';
import { BillingInformationDialog } from './BillingInformationDialog';
import { ScreenManagementDialog } from './ScreenManagementDialog';
import { useBillingDialogStore } from '../stores/billing-dialog-store';

/**
 * Mounts all billing dialogs at app level.
 * They open/close via billing-dialog-store (activeDialog enum).
 * Mounted in AppShell when VITE_USE_NATIVE_BILLING=true.
 */
export function BillingDialogs() {
  return (
    <>
      <SubscriptionWizardDialog />
      <DowngradeDialog />
      <UpgradeRequiredDialog />
      <AddonRequiredDialog />
      <AddonUpdateDialog />
      <UnpaidInvoicesDialog />
      <EnableTeamBillingDialog />
      <BillingInformationDialog />
      <ScreenManagementDialogFromStore />
    </>
  );
}

/**
 * SP53 — store-driven OVERLAY mount of ScreenManagementDialog.
 * Opened via openScreenManagement({ subAccountId, teamId, totalPairedScreens, messageKey })
 * by callers outside the wizard (e.g. handleSelectFree on LIMIT_QUANTITY error,
 * DowngradeDialog on paid → free downgrade limit). It is NOT part of the activeDialog enum
 * so it stacks on top of the subscription wizard without closing it (scio1 parity).
 * The wizard's existing local mount continues to handle the in-wizard decrease-screen-count
 * flow independently.
 *
 * Exported so `InactiveAccountDialog` (paywall lockout) can mount it directly without
 * pulling in the rest of `<BillingDialogs />`. Trial-expired users hitting the Free-plan
 * downgrade 5555 path need this component in the rendered tree to actually render the
 * dialog when the global store opens it.
 */
export function ScreenManagementDialogFromStore() {
  const open = useBillingDialogStore((s) => s.screenManagementOpen);
  const ctx = useBillingDialogStore((s) => s.screenManagementContext);
  const close = useBillingDialogStore((s) => s.closeScreenManagement);
  return (
    <ScreenManagementDialog
      open={open}
      onClose={close}
      totalPairedScreens={(ctx.totalPairedScreens as number) ?? 0}
      subAccountId={ctx.subAccountId as string | undefined}
      teamId={ctx.teamId as string | undefined}
      messageKey={ctx.messageKey as string | undefined}
    />
  );
}
